import parseGiftcardToCartItem from '~/utils/parseGiftcardToCartItem'
import type {
  EventDate,
  EventDescription,
  Pricings,
} from '~/composables/types/event'
import type { GiftcardAddon, GiftcardCartItem } from '~/composables/types/cart'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'

interface AddGiftcardToCartParams {
  event: EventDescription
  pricings: Pricings
  giftcardType: 'VALUE' | 'EVENT' | 'SPECIAL'
  quantity?: number
  selectedTime?: EventDate
  /** Delivery option (PDF / CARD / BOX). Defaults to PDF inside the cart item parser. */
  giftcardAddon?: GiftcardAddon
  /** Embedded checkout only ever holds a single line, so the cart is reset first. */
  clearCartFirst?: boolean
}

/**
 * Shared "add this giftcard to the cart" flow: builds the cart item, applies the
 * chosen delivery option, opens the cart sidebar and reports the conversion.
 */
export const useGiftcardAddToCart = () => {
  const {
    addItem,
    clear: clearCart,
    toggleGiftcardAddon,
    isInCart,
    cart,
  } = useCartWithSubdomainLogic()
  const { toggleCartSidebar } = useUiState()
  const { sendAddToCartEvent } = useAlgoliaConversion()
  const { shouldQuickCheckout } = useBookNowExperiment()
  const localePath = useLocalePath()

  const addGiftcardToCart = async ({
    event,
    pricings,
    giftcardType,
    quantity = 1,
    selectedTime,
    giftcardAddon,
    clearCartFirst = false,
  }: AddGiftcardToCartParams): Promise<GiftcardCartItem | undefined> => {
    const cartItem = parseGiftcardToCartItem(
      event,
      quantity,
      ref(selectedTime),
      pricings,
      giftcardType
    )

    if (!cartItem) return undefined

    if (giftcardAddon) {
      cartItem.giftcardAddon = giftcardAddon
    }

    if (clearCartFirst) {
      clearCart()
    }

    // Snapshot before adding: quick-checkout only on the first item (batching
    // rule), and adding would otherwise make the cart non-empty.
    const cartHasItems = (cart.value?.items?.length ?? 0) > 0

    await addItem(cartItem)

    // Re-adding a giftcard that is already in the cart merges the quantities and
    // keeps the existing line's delivery option, so re-apply the picked one.
    if (giftcardAddon) {
      toggleGiftcardAddon(cartItem, giftcardAddon)
    }

    sendAddToCartEvent(event)

    // "Book Now" routing (KFT-3914). Today every giftcard type resolves to
    // add-to-cart (BOOK_NOW_EVENT_GIFTCARDS is off), so this keeps the current
    // sidebar flow. It's the single seam to flip Event giftcards to quick
    // checkout once the personalization flow ships.
    const goToCheckout =
      shouldQuickCheckout({ giftcardType, cartHasItems }) && isInCart(cartItem)

    if (goToCheckout) {
      await navigateTo(localePath('/checkout'))
    } else {
      toggleCartSidebar()
    }

    return cartItem
  }

  return { addGiftcardToCart }
}
